<template>
    <div class="checkout-canvas">
      <!-- Main Responsive Grid Wrapper -->
      <main class="container py-4 py-lg-5">
        <div class="row layout-desktop-grid">
          
          <!-- 📝 Left Core Form Column: User Address & Selection Options -->
          <section class="col-12 col-lg-8 column-main-entry">
            
            <!-- SECTION A: ADDRESS MANAGEMENT BLOCK -->
            <div class="checkout-card-section mb-4">
              <h2 class="section-title-header mb-3">ADDRESS</h2>
              
              <div class="address-display-box d-flex align-items-center justify-content-between p-3">
                <div class="address-details-meta d-flex align-items-center gap-3" style="gap: 10px;">
                  <!-- Location Geolocation Icon Vector Container -->
                  <div class="location-icon-wrapper d-flex align-items-center justify-content-center">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="map-marker-pin">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div class="text-content">
                    <h4 class="address-type-title m-0">Home</h4>
                    <p class="address-string-text text-muted m-0 mt-1">
                      {{ address.street }} {{ address.cityStateCountry }}
                    </p>
                  </div>
                </div>
                <!-- Action Trigger Interceptor Button -->
                <button class="edit-action-link-btn" @click="triggerAddressEdit">Edit</button>
              </div>
            </div>
  
            <!-- SECTION B: DELIVERY METHOD METHOD TRACK -->
            <div class="checkout-card-section mb-4">
              <h2 class="section-title-header mb-3">DELIVERY METHOD</h2>
              <div class="delivery-methods-stack d-flex flex-column gap-3">
                
                <!-- Option Block 1: Standard Delivery Shipping Track -->
                <div 
                  class="delivery-method-option-card d-flex align-items-center justify-content-between p-3"
                  :class="{ 'is-selected': selectedDeliveryMethod === 'standard' }"
                  @click="selectedDeliveryMethod = 'standard'"
                >
                  <div class="d-flex align-items-center gap-3" style="gap: 10px;">
                    <div class="custom-radio-button-outer d-flex align-items-center justify-content-center">
                      <span class="radio-inner-dot" v-if="selectedDeliveryMethod === 'standard'"></span>
                    </div>
                    <div class="method-meta">
                      <h4 class="method-title m-0">Standard Delivery</h4>
                      <p class="method-estimation text-muted m-0 mt-1">Est. delivery: 3-5 business days</p>
                    </div>
                  </div>
                  <span class="price-indicator-tag font-weight-bold">Free</span>
                </div>
  
                <!-- Option Block 2: Express Delivery Shipping Track -->
                <div 
                  class="delivery-method-option-card d-flex align-items-center justify-content-between p-3"
                  :class="{ 'is-selected': selectedDeliveryMethod === 'express' }"
                  @click="selectedDeliveryMethod = 'express'"
                >
                  <div class="d-flex align-items-center gap-3" style="gap: 10px;">
                    <div class="custom-radio-button-outer d-flex align-items-center justify-content-center">
                      <span class="radio-inner-dot" v-if="selectedDeliveryMethod === 'express'"></span>
                    </div>
                    <div class="method-meta">
                      <h4 class="method-title m-0">Express Delivery</h4>
                      <p class="method-estimation text-muted m-0 mt-1">Est. delivery: 1-2 business days</p>
                    </div>
                  </div>
                  <span class="price-indicator-tag font-weight-bold">
                    ₦{{ formatPrice(deliveryFees.express) }}
                  </span>
                </div>
  
              </div>
            </div>
  
            <!-- 💳 SECTION C: PAYMENT PARAMETERS LOCK -->
            <div class="checkout-card-section mb-5">
              <h2 class="section-title-header mb-3">PAYMENT METHOD</h2>
              
              <div class="payment-method-toggle-card d-flex align-items-center justify-content-between p-3">
                <div class="payment-details-meta d-flex align-items-center gap-3" style="gap: 10px;">
                  <div class="safe-wallet-logo-wrapper d-flex align-items-center justify-content-center">
                    <!-- Custom SAFE Wallet Branding Representation Glyphs -->
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                      <line x1="2" y1="10" x2="22" y2="10"></line>
                    </svg>
                  </div>
                  <div class="text-content">
                    <h4 class="payment-system-title m-0">Pay with SAFE</h4>
                    <p class="wallet-balance-text text-muted m-0 mt-1">
                      Balance: <span class="font-weight-bold">₦{{ formatPrice(walletBalance) }}</span>
                    </p>
                  </div>
                </div>
                
                <!-- Reactive Toggle Switch Mechanism Component -->
                <button 
                  class="ui-toggle-switch-btn"
                  :class="{ 'is-active-on': payWithSafeActive }"
                  @click="payWithSafeActive = !payWithSafeActive"
                  :aria-label="'Toggle payment via SAFE wallet system'"
                >
                  <span class="toggle-slider-nob"></span>
                </button>
              </div>
            </div>
  
          </section>
  
          <!-- 🛒 Right Supplementary Desktop Columns: Placeholder for Order Summary Part 2 -->
          <!-- d-none d-lg-block -->
          <section class="col-12 col-lg-4 column-summary-dock">
            <div class="desktop-summary-sticky-card p-4">
                <div class="order-summary-section">
                    <!-- Section Title Header Tracking Layout Spec -->
                    <h2 class="section-title-header mb-3">ORDER SUMMARY</h2>

                    <!-- Core Checkout Financial Card Block Container -->
                    <div class="summary-display-card p-3 p-md-4">
                    
                    <!-- ITEM ARRAY LIST STREAM -->
                    <div class="items-checkout-list d-flex flex-column gap-4 pb-3 border-bottom mb-3">
                        <article 
                        v-for="item in cartItems" 
                        :key="item.id" 
                        class="item-checkout-row d-flex justify-content-between align-items-start gap-3"
                        >
                        <!-- Left Content Cluster: Image & Description Meta -->
                        <div class="item-meta-details d-flex gap-3" style="gap: 6px;">
                            <div class="item-thumbnail-canvas d-flex align-items-center justify-content-center">
                            <img :src="item.imageUrl" :alt="item.title" class="img-fluid item-thumb-img" />
                            </div>
                            <div class="item-typography-stack">
                            <h4 class="item-title-heading m-0">{{ item.title }}</h4>
                            <p class="item-qty-label text-muted m-0 mt-1">Qty: {{ item.quantity }}</p>
                            </div>
                        </div>
                        
                        <!-- Right Content Financial Price Tag -->
                        <span class="item-price-tag font-weight-bold">
                            ₦{{ formatPrice(item.price * item.quantity) }}
                        </span>
                        </article>
                    </div>

                    <!-- 📊 SUB-TOTAL CALCULATION ROWS MATRIX -->
                    <div class="calculation-summary-lines d-flex flex-column gap-2 pb-3 border-bottom mb-3">
                        
                        <div class="calc-row d-flex justify-content-between align-items-center">
                        <span class="calc-property-label text-muted">Subtotal</span>
                        <span class="calc-value-tag font-weight-bold">₦{{ formatPrice(computedSubtotal) }}</span>
                        </div>

                        <div class="calc-row d-flex justify-content-between align-items-center">
                        <span class="calc-property-label text-muted">Delivery ({{ deliveryMethodLabel }})</span>
                        <span class="calc-value-tag font-weight-bold">
                            {{ activeDeliveryCost === 0 ? '₦0.00' : '₦' + formatPrice(activeDeliveryCost) }}
                        </span>
                        </div>

                        <div class="calc-row d-flex justify-content-between align-items-center">
                        <span class="calc-property-label text-muted">Taxes</span>
                        <span class="calc-value-tag font-weight-bold">₦{{ formatPrice(computedTaxes) }}</span>
                        </div>

                    </div>

                    <!-- 🏆 GRAND TOTAL FINAL DOCK BLOCK -->
                    <div class="grand-total-row d-flex justify-content-between align-items-baseline pt-2">
                        <h3 class="total-main-label m-0">Total</h3>
                        <span class="total-financial-highlight font-weight-bold">
                        ₦{{ formatPrice(computedGrandTotal) }}
                        </span>
                    </div>

                    </div>
                </div>
            </div>
          </section>
  
        </div>
      </main>
  
      <!-- 📱 Mobile Floating Bottom Sticky Action Dock (Synchronizes with Checkout Rules) -->
      <footer class="mobile-sticky-action-bar-dock p-3 border-top w-100">
        <button 
          class="confirm-pay-master-btn w-100 d-flex align-items-center justify-content-center gap-2"
          :disabled="isSubmittingPayment"
          @click="dispatchCheckoutPipeline"
        >
          <span>{{ isSubmittingPayment ? 'Processing Terminal...' : 'Confirm & Pay' }}</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="arrow-forward-icon">
            <line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </button>
      </footer>
    </div>
  </template>
  
  <script>
  // @ts-nocheck
  import Vue from 'vue';
  
  export default Vue.extend({
    name: 'Checkout',
    props: {
        // Allows parent layout settings to update delivery fees dynamically based on Part 1 selections
        currentDeliveryMethod: {
        type: String,
        default: 'standard' // 'standard' or 'express'
        }
    },
    data() {
      return {
        selectedDeliveryMethod: 'standard', // Maps 'standard' or 'express' option matrices
        payWithSafeActive: true,
        isSubmittingPayment: false,
        walletBalance: 2450.00,
        
        deliveryFees: {
          standard: 0.00,
          express: 15.00
        },
        
        address: {
          street: '123 Maple Avenue Victoria',
          cityStateCountry: 'Island, Lagos Nigeria'
        },
        taxRatePercentage: 0.05,
        cartItems: [
            {
            id: "item-smart-therm",
            title: 'Smart Thermostat Pro',
            quantity: 1,
            price: 199.00,
            imageUrl: 'https://unsplash.com'
            },
            {
            id: "item-door-sens",
            title: 'Security Door Sensors',
            quantity: 2,
            price: 24.99, // 24.99 * 2 = 49.98 total price parameter alignment matching image metrics
            imageUrl: 'https://unsplash.com'
            }
        ],
      };
    },
    computed: {
        computedSubtotal() {
            return this.cartItems.reduce((accumulator, item) => accumulator + (item.price * item.quantity), 0);
        },
        activeDeliveryCost() {
            // Switches calculation weight depending on your selection state inside Part 1 variables
            return this.currentDeliveryMethod === 'express' ? 15.00 : 0.00;
        },
        deliveryMethodLabel() {
            return this.currentDeliveryMethod === 'express' ? 'Express' : 'Standard';
        },
        computedTaxes() {
            // Calculates exactly 12.45 tax matching mockup payload values: 248.98 * 0.05 = 12.449 (rounds to 12.45)
            return this.computedSubtotal * this.taxRatePercentage;
        },
        computedGrandTotal() {
            return this.computedSubtotal + this.activeDeliveryCost + this.computedTaxes;
        }
    },
    methods: {
      formatPrice(val) {
        return val.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      triggerAddressEdit() {
        console.log('[Address Action]: Open contextual alignment management modal overlay.');
        this.$emit('edit-address');
      },
      dispatchCheckoutPipeline() {
        this.isSubmittingPayment = true;
        console.log(`[Transaction Lock]: Processing pipeline authorization routing for strategy: ${this.selectedDeliveryMethod}`);
        
        setTimeout(() => {
          this.isSubmittingPayment = false;
          this.$emit('checkout-complete', {
            deliveryMethod: this.selectedDeliveryMethod,
            usedSafeWallet: this.payWithSafeActive
          });
        }, 1200);
      }
    }
  });
  </script>

<style lang="scss" scoped>
// System Layout Variables Matrix
$font-sans: 'Public Sans', system-ui, -apple-system, sans-serif;
$color-brand-blue: #0029b3; // High contrast deep primary layout action blue hex token matching mockup
$color-bg-canvas: #fdfefe;
$color-card-border: #e2e8f0;
$color-text-header: #7e8b9b; // Light neutralized gray for the uppercase card descriptors headers
$color-dark-main: #0c1a30;
$color-muted-main: #64748b;
$breakpoint-lg: 1024px;
$color-bg-base: #f8fafc;

.checkout-canvas {
  font-family: $font-sans;
  background-color: #fafbfc;
  color: $color-dark-main;
  min-height: 100vh;
  padding-bottom: 30px; // Clear structural margin padding blocks mobile floating button overlaps
  box-sizing: border-box;
}

/* ─── 🏛️ STRUCTURE SECTIONS TILES AND GENERAL CARDS ─── */
.section-title-header {
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: $color-text-header;
  text-transform: uppercase;
  margin: 0;
}

.delivery-method-option-card{
    margin-bottom: 15px;
}

.address-display-box, 
.delivery-method-option-card, 
.payment-method-toggle-card {
  background-color: #ffffff;
  border: 1px solid $color-card-border;
  border-radius: 16px;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.01);
  box-sizing: border-box;
}

/* 📍 ADDRESS INTERACTION PRIMITIVES */
.location-icon-wrapper {
  width: 46px;
  height: 46px;
  background-color: #eff6ff; // Soft blue tone highlight icon backup frame circular container
  border-radius: 50%;
  color: $color-brand-blue;
  flex-shrink: 0;
}

.address-type-title {
  font-size: 1rem;
  font-weight: 700;
  color: $color-dark-main;
  text-align: left;
}

.address-string-text {
  font-size: 0.9rem;
  line-height: 1.4;
  text-align: left;
}

.edit-action-link-btn {
  background: transparent;
  border: none;
  font-weight: 700;
  color: $color-brand-blue;
  font-size: 0.95rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
}

/* 🚚 DELIVERY OPTIONS SELECTION SYSTEM TRACK */
.delivery-method-option-card {
  cursor: pointer;
  border-width: 1.5px; // Thicker line layout to match the select indicator state easily

  &:hover:not(.is-selected) {
    border-color: lighten($color-muted-main, 20%);
  }

  &.is-selected {
    border-color: $color-brand-blue;
    background-color: #f6f9ff; // Blueprint active choice box background color transformation parameter
  }
}

.custom-radio-button-outer {
  width: 22px;
  height: 22px;
  border: 2px solid var(--color-border, #cbd5e1);
  border-radius: 50%;
  flex-shrink: 0;
  background-color: #ffffff;
  transition: border-color 0.2s ease;

  .is-selected & {
    border-color: $color-brand-blue;
  }
}


.radio-inner-dot {
  width: 10px;
  height: 10px;
  background-color: $color-brand-blue;
  border-radius: 50%;
  display: block;
}

.method-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: $color-dark-main;
  text-align: left;
}

.method-estimation {
  font-size: 0.85rem;
  text-align: left;
}

.price-indicator-tag {
  font-size: 0.95rem;
  color: $color-dark-main;
}

/* 💳 PAYMENT METHOD SWITCH TOGGLE PRIMITIVES */
.safe-wallet-logo-wrapper {
  width: 46px;
  height: 46px;
  background-color: #e0e7ff;
  border-radius: 50%;
  color: $color-brand-blue;
  flex-shrink: 0;
}

.payment-system-title {
  font-size: 1rem;
  font-weight: 700;
  color: $color-dark-main;
  text-align: left;
}

.wallet-balance-text {
  font-size: 0.9rem;
}

/* UI TOGGLE SWITCH FORM CONTROL SLIDER */
.ui-toggle-switch-btn {
  width: 50px;
  height: 28px;
  background-color: #cbd5e1;
  border: none;
  border-radius: 14px;
  position: relative;
  cursor: pointer;
  transition: background-color 0.25s ease;
  padding: 0;
  outline: none;

  &.is-active-on {
    background-color: $color-brand-blue;
  }
}

.toggle-slider-nob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 22px;
  height: 22px;
  background-color: #ffffff;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  
  .is-active-on & {
    transform: translateX(22px);
  }
}

/* 📱 MOBILE FLOATING STICKY DOCK MECHANICS */
.mobile-sticky-action-bar-dock {
  position: sticky;
  bottom: 0;
  left: 0;
  background-color: #ffffff;
  z-index: 90;
  box-shadow: 0 -4px 12px rgba(15, 23, 42, 0.03);
  box-sizing: border-box;
}

.confirm-pay-master-btn {
  background-color: $color-brand-blue;
  color: #ffffff;
  border: none;
  height: 54px; // Production physical target size matrix parameters for touch screens
  border-radius: 12px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: lighten($color-brand-blue, 5%);
  }

  &:disabled {
    background-color: $color-muted-main;
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.arrow-forward-icon {
  transition: transform 0.2s ease;
  
  .confirm-pay-master-btn:hover & {
    transform: translateX(4px); // Micro-interaction effect on hovering action pathways
  }
}

/* ─── 📱 WIDESCREEN DESKTOP VIEWPORTS MEDIA QUERIES (No Mixins) ─── */
@media (min-width: $breakpoint-lg) {
  .checkout-canvas {
    padding-bottom: 0; // Hides loose mobile padding blocks on structural widescreen monitors
  }

  .mobile-sticky-action-bar-dock {
    // display: none !important; // Excludes mobile layout sticky drawer element completely
  }

  .desktop-summary-sticky-card {
    background-color: #ffffff;
    border: 1px solid $color-card-border;
    border-radius: 16px;
    min-height: 280px;
    position: sticky;
    top: 2rem; // Follows scroll path patterns smoothly on high resolution desktops
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--shadow-sm);
  }
}

.order-summary-section {
  font-family: $font-sans;
  color: $color-dark-main;
  box-sizing: border-box;
}

.section-title-header {
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: $color-text-header;
  text-transform: uppercase;
  margin: 0;
  text-align: left;
}

/* ─── 📦 CORE FINANCIAL CARD PANEL ─── */
.summary-display-card {
  background-color: #ffffff;
  border: 1px solid $color-card-border;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.01);
  box-sizing: border-box;
}

/* ITEM GRID IMAGES ROWS STREAM */
.item-checkout-row {
  font-size: 0.95rem;
  gap: 6px;
  margin-bottom: 18px;
}

.item-thumbnail-canvas {
//   width: 54px;
//   height: 54px;
  width: 40px;
  height: 40px;
  background-color: $color-bg-base;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;

  .item-thumb-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.item-title-heading {
  font-size: 0.95rem;
  font-weight: 700;
  color: $color-dark-main;
  line-height: 1.3;
}

.item-qty-label {
  font-size: 0.85rem;
}

.item-price-tag {
  font-size: 0.95rem;
  color: $color-dark-main;
}

/* 📊 SUB-TOTAL LINE ITEM COLUMNS */
.calc-row {
  font-size: 0.95rem;

  .calc-property-label {
    font-weight: 500;
  }

  .calc-value-tag {
    color: $color-dark-main;
  }
}

/* 🏆 GRAND TOTAL FOOT DISPLAY HERO BLOCK */
.total-main-label {
  font-size: 1.25rem;
  font-weight: 800;
  color: $color-dark-main;
  letter-spacing: -0.01em;
}

.total-financial-highlight {
  font-size: 1.55rem;
  font-weight: 800;
  color: $color-brand-blue; // Deep blue promo highlight matching mockup design criteria exactly
  letter-spacing: -0.02em;
}

/* ─── 📱 MOCKUP ADAPTIVE VIEWPORT OVERRIDES ─── */
@media (min-width: 768px) {
  .summary-display-card {
    padding: 1.5rem !important;
  }

  .item-thumbnail-canvas {
    width: 40px;
     height: 40px;
   // width: 64px;
   // height: 64px; // Gives product thumbnails optimal canvas spacing on roomy screens
  }

  .item-title-heading {
    font-size: 1.05rem;
  }

  .total-financial-highlight {
    font-size: 1.75rem;
  }
}
</style>

  